import type { ReactNode } from 'react';
import type { BookingStatus, QuoteStatus } from '../../lib/types';

export function StatCard({
  label,
  value,
  icon,
  accent = false,
}: {
  label: string;
  value: string | number;
  icon: ReactNode;
  accent?: boolean;
}) {
  return (
    <div className={`card p-5 ${accent ? 'border-accent-500/40' : ''}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{label}</span>
        <span className="text-accent-400">{icon}</span>
      </div>
      <div className="mt-2 font-display text-3xl font-extrabold text-white">{value}</div>
    </div>
  );
}

const bookingColors: Record<BookingStatus, string> = {
  pending: 'border-amber-400/40 bg-amber-400/10 text-amber-300',
  confirmed: 'border-sky-400/40 bg-sky-400/10 text-sky-300',
  in_progress: 'border-violet-400/40 bg-violet-400/10 text-violet-300',
  completed: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  cancelled: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-400',
};

export function BookingStatusBadge({ status }: { status: BookingStatus }) {
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${bookingColors[status] ?? bookingColors.pending}`}>
      {status.replace('_', ' ')}
    </span>
  );
}

const quoteColors: Record<QuoteStatus, string> = {
  new: 'border-amber-400/40 bg-amber-400/10 text-amber-300',
  contacted: 'border-sky-400/40 bg-sky-400/10 text-sky-300',
  quoted: 'border-violet-400/40 bg-violet-400/10 text-violet-300',
  won: 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300',
  lost: 'border-zinc-500/40 bg-zinc-500/10 text-zinc-400',
};

export function QuoteStatusBadge({ status }: { status: QuoteStatus }) {
  return (
    <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${quoteColors[status] ?? quoteColors.new}`}>
      {status}
    </span>
  );
}

export function Field({ label, children, span2 = false }: { label: string; children: ReactNode; span2?: boolean }) {
  return (
    <div className={span2 ? 'sm:col-span-2' : ''}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}

export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-night-950/80 p-4 backdrop-blur-sm sm:p-8" onClick={onClose}>
      <div className="card w-full max-w-2xl bg-night-900 p-6 sm:p-8" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-xl font-bold text-white">{title}</h2>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

export function EmptyState({ text }: { text: string }) {
  return <p className="py-16 text-center text-sm text-zinc-500">{text}</p>;
}
